import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { useTapOnce } from '../ui/useTapOnce';
import { sendOnce } from '../tickets/sendOnce';
import { Button } from '../ui/Button';
import { priceCentsOf } from './JobPriceEditor';

/**
 * Add a charge to a T&M job (Inventory v2 12c, decision 10): a trip fee, disposal, a permit. It
 * waits under Left to bill and goes on an invoice through the bill sheet, like a part. One save
 * per tap.
 */
export function ChargeForm({ jobId, onDone }: { jobId: string; onDone: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const lock = useTapOnce();
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [taxable, setTaxable] = useState(false);
  const save = useMutation(
    trpc.partsBilling.addCharge.mutationOptions({
      onSettled: () => lock.release(),
      onSuccess: () => {
        void qc.invalidateQueries();
        onDone();
      },
    }),
  );
  const cents = priceCentsOf(amount);
  const name = description.trim();
  const amountOk = cents !== null && cents > 0;
  const ok = name !== '' && amountOk;
  // Why Add charge is off, said beside it: what is missing, or the amount that can't be sent.
  const need =
    amount !== '' && !amountOk
      ? 'Type an amount in dollars, above $0.'
      : name === '' || amount === ''
        ? 'A charge needs what it is for and an amount.'
        : null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-md)] border border-border bg-surface-2 p-2 text-sm">
      <p className="basis-full text-xs font-semibold">Add a charge to this job</p>
      <input
        autoFocus
        className="h-9 min-w-[12rem] flex-1 rounded-[var(--radius-sm)] border border-border bg-surface px-2"
        placeholder="Trip fee, disposal, permit…"
        aria-label="What the charge is for"
        value={description}
        maxLength={200}
        readOnly={save.isPending}
        onChange={(e) => setDescription(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onDone();
        }}
      />
      <span className="inline-flex items-center gap-1">
        <span aria-hidden>$</span>
        <input
          className="h-9 w-24 rounded-[var(--radius-sm)] border border-border bg-surface px-2 tabular-nums"
          inputMode="decimal"
          placeholder="45.00"
          aria-label="Amount"
          aria-invalid={amount !== '' && !amountOk ? true : undefined}
          aria-describedby={amount !== '' && !amountOk ? 'charge-need' : undefined}
          value={amount}
          readOnly={save.isPending}
          onChange={(e) => setAmount(e.target.value)}
        />
      </span>
      <label className="inline-flex items-center gap-1 text-xs">
        <input
          type="checkbox"
          checked={taxable}
          disabled={save.isPending}
          onChange={(e) => setTaxable(e.target.checked)}
        />
        Taxable
      </label>
      {need ? (
        <span
          id="charge-need"
          className={`basis-full text-xs ${amount !== '' && !amountOk ? 'text-[var(--sw-danger-text)]' : 'text-muted-foreground'}`}
        >
          {need}
        </span>
      ) : null}
      <span className="basis-full text-xs text-muted-foreground">
        It waits under Left to bill and goes on an invoice through the bill sheet, like a
        part.
      </span>
      {save.error ? (
        <span role="alert" className="basis-full text-xs text-[var(--sw-danger-text)]">
          {save.error.message}
        </span>
      ) : null}
      <span className="ml-auto flex gap-2">
        <Button variant="ghost" onClick={onDone} disabled={save.isPending}>
          Cancel
        </Button>
        <Button
          disabled={!ok || save.isPending}
          aria-describedby={need ? 'charge-need' : undefined}
          onClick={() =>
            ok &&
            sendOnce(lock, save.mutate, {
              jobId,
              description: name,
              amountCents: cents!,
              taxable,
            })
          }
        >
          {save.isPending ? 'Adding…' : 'Add charge'}
        </Button>
      </span>
    </div>
  );
}
